import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';

// Components - REMOVED StatusBar
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import ScrollToTop from './components/common/ScrollToTop';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Experience from './pages/Experience';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Services from './pages/Services';
import Hire from './pages/Hire';
import Mentorship from './pages/Mentorship';
import OpenSource from './pages/OpenSource';
import Testimonials from './pages/Testimonials';
import Contact from './pages/Contact';

// Admin
import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/Dashboard';
import AdminProjects from './admin/Projects';
import AdminBlog from './admin/Blog';
import AdminExperience from './admin/Experience';
import AdminSkills from './admin/Skills';
import AdminAbout from './admin/About';
import AdminHomepage from './admin/Homepage';
import AdminContacts from './admin/Contacts';
import AdminMentorship from './admin/MentorshipRequests';
import AdminNotifications from './admin/Notifications';
import AdminAnalytics from './admin/Analytics';
import AdminMedia from './admin/MediaLibrary';
import AdminSEO from './admin/SEO';
import AdminResume from './admin/ResumeManager';
import AdminSettings from './admin/Settings';
import AdminLogin from './admin/Login';
import AdminHireRequests from './admin/HireRequests';

// Context
import { AuthProvider } from './context/AuthContext';

// Styles
import './styles/globals.css';
import 'react-toastify/dist/ReactToastify.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <div className="min-h-screen bg-dark-950 text-gray-200">
              <Routes>
                {/* Admin Login - No Layout */}
                <Route path="/admin/login" element={<AdminLogin />} />
                
                {/* Main Layout - NO STATUS BAR */}
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/experience" element={<Experience />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/:slug" element={<ProjectDetail />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/hire" element={<Hire />} />
                  <Route path="/mentorship" element={<Mentorship />} />
                  <Route path="/open-source" element={<OpenSource />} />
                  <Route path="/testimonials" element={<Testimonials />} />
                  <Route path="/contact" element={<Contact />} />
                </Route>
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="projects" element={<AdminProjects />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="experience" element={<AdminExperience />} />
                  <Route path="skills" element={<AdminSkills />} />
                  <Route path="about" element={<AdminAbout />} />
                  <Route path="homepage" element={<AdminHomepage />} />
                  <Route path="contacts" element={<AdminContacts />} />
                  <Route path="hire-requests" element={<AdminHireRequests />} />
                  <Route path="mentorship" element={<AdminMentorship />} />
                  <Route path="notifications" element={<AdminNotifications />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="media" element={<AdminMedia />} />
                  <Route path="seo" element={<AdminSEO />} />
                  <Route path="resume" element={<AdminResume />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
              </Routes>
              
              <ToastContainer
                position="bottom-right"
                theme="dark"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
            </div>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

// Main Layout Component - NO STATUS BAR
function MainLayout() {
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
    </>
  );
}

export default App;